<template>
  <div>
    <b-row>
      <b-col md="12">
        <div class="text-left mb-3">
          <b-badge pill variant="info">Offline Modus</b-badge>
          <h4 class="font-weight-bold ml-1 mt-2">
            {{ order.ordernumber }} - {{ order.customer_company?.name || 'Ukjent kunde' }}
            <span v-if="order.site && order.site.name">{{ '(' + order.site.name + ')' }}</span>
          </h4>
        </div>
        
        <div class="text-right mb-3">
          <b-button variant="default" @click="goBack()">Tilbake</b-button>
        </div>
      </b-col>
    </b-row>

    <b-alert v-if="!orderAvailable" show variant="danger" class="mt-3">
      Ingen lokal kopi av denne ordren er tilgjengelig offline. Koble til nett for å hente data.
    </b-alert>

    <b-alert v-if="$isOnline" show variant="success" class="mt-3">
      Du er nå tilkoblet. Du kan gå tilbake til normal redigeringsmodus eller laste opp utkastene dine.
      <div class="mt-2">
        <b-button size="sm" variant="outline-success" @click="goToOnlineEditor">
          Gå til normal editor
        </b-button>
      </div>
    </b-alert>

    <b-row v-if="orderAvailable">
      <b-col class="col-12">
        <b-card no-body>
          <b-tabs card>
            <!-- FLS Tab -->
            <b-tab v-if="order.type == 'Avlusning'" title="FLS" active>
              <b-card-text>
                <b-row>
                  <b-col md="3">
                    <b-card bg-variant="light">
                      <h6>FLS ({{ fls.lines }} {{ fls.lines === 1 ? 'linje' : 'linjer' }})</h6>
                      <hr>

                      <p>Totalt antall fisk (lokalt):</p>
                      <p>{{ localFlsSumFish | numeralFormat("0,0[.]00") }}</p>

                      <p>Total biomasse (lokalt):</p>
                      <p>{{ localFlsSumBiomass | numeralFormat("0,0[.]00") }} kg</p>

                      <hr>

                      <b-row>
                        <b-col>
                          <label>Fra Merd</label>
                          <b-form-input class="mb-3" v-model="fls.from_cage" placeholder="Merd"></b-form-input>
                        </b-col>
                        <b-col>
                          <label>Til Merd</label>
                          <b-form-input class="mb-3" v-model="fls.to_cage" placeholder="Merd"></b-form-input>
                        </b-col>
                      </b-row>

                      <label>Antall fisk</label>
                      <b-form-input class="mb-3" v-model.number="fls.fishcount" type="number" placeholder="Antall fisk"></b-form-input>

                      <label>Biomasse (kg)</label>
                      <b-input-group class="mb-3">
                        <b-input type="number" v-model.number="fls.biomass"></b-input>
                        <b-input-group-append>
                          <b-input-group-text>kg</b-input-group-text>
                        </b-input-group-append>
                      </b-input-group>

                      <label>Kommentar</label>
                      <b-form-textarea class="mb-3" v-model="fls.comment" placeholder="Kommentar" rows="3" max-rows="6"></b-form-textarea>

                      <div>
                        <b-form-checkbox v-model="fls.completed" :value="true" :unchecked-value="false">
                          Avsluttet
                        </b-form-checkbox>
                      </div>

                      <b-collapse id="settingsFLS" class="mt-2">
                        <hr>
                        <label>Antall linjer</label>
                        <b-form-input class="mr-3 mb-3" v-model.number="fls.lines" type="number" max="5" placeholder="Antall linjer"></b-form-input>

                        <label>Temperatur i luft</label>
                        <b-form-input class="mr-3 mb-3" v-model.number="fls.temperature_air" type="number" placeholder="Temperatur i luft"></b-form-input>

                        <label>Temperatur i sjø</label>
                        <b-form-input class="mr-3 mb-3" v-model.number="fls.temperature_sea" type="number" placeholder="Temperatur i sjø"></b-form-input>
                      </b-collapse>

                      <b-form inline class="mt-3">
                        <b-button class="mr-1" @click="saveFlsOffline()" variant="primary">
                          Lagre som utkast
                        </b-button>
                        <b-button v-b-toggle.settingsFLS class="card-link" variant="link">Innstillinger</b-button>
                      </b-form>
                    </b-card>
                  </b-col>

                  <b-col md="9">
                    <b-card>
                      <h6>FLS Utkast (lagret lokalt)</h6>
                      
                      <b-alert v-if="offlineFlsDrafts.length === 0" show variant="info">
                        Ingen FLS-utkast lagret ennå. Legg til FLS-data ved å fylle ut skjemaet til venstre.
                      </b-alert>

                      <!-- FLS Line inputs -->
                      <div v-if="fls.lines >= 1" class="mb-3">
                        <b-row>
                          <b-col>
                            <strong>Linje 1</strong>
                            <b-progress class="mb-1" height="0.5rem" :value="fls.data.line1_ejector" :max="1" variant="danger" animated></b-progress>
                            <b-progress class="mb-1" height="0.5rem" :value="fls.data.line1_flushpressure" :max="1" variant="warning" animated></b-progress>
                            <b-progress class="mb-1" height="0.5rem" :value="fls.data.line1_oxygen" :max="200" variant="info" animated></b-progress>
                          </b-col>
                          <b-col class="text-center">
                            <label>Ejektor trykk</label>
                            <b-form-spinbutton type="number" class="mb-3" :min="0.01" :max="1" :step="0.01" v-model="fls.data.line1_ejector"></b-form-spinbutton>
                          </b-col>
                          <b-col class="text-center">
                            <label>Spyletrykk</label>
                            <b-form-spinbutton type="number" class="mb-3" :min="0.01" :max="1" :step="0.01" v-model="fls.data.line1_flushpressure"></b-form-spinbutton>
                          </b-col>
                          <b-col class="text-center">
                            <label>Oksygen</label>
                            <b-form-spinbutton type="number" class="mb-3" :min="1" :max="200" :step="1" v-model="fls.data.line1_oxygen"></b-form-spinbutton>
                          </b-col>
                        </b-row>
                        <hr />
                      </div>

                      <div v-if="fls.lines >= 2" class="mb-3">
                        <b-row>
                          <b-col>
                            <strong>Linje 2</strong>
                            <b-progress class="mb-1" height="0.5rem" :value="fls.data.line2_ejector" :max="1" variant="danger" animated></b-progress>
                            <b-progress class="mb-1" height="0.5rem" :value="fls.data.line2_flushpressure" :max="1" variant="warning" animated></b-progress>
                            <b-progress class="mb-1" height="0.5rem" :value="fls.data.line2_oxygen" :max="200" variant="info" animated></b-progress>
                          </b-col>
                          <b-col class="text-center">
                            <label>Ejektor trykk</label>
                            <b-form-spinbutton type="number" class="mb-3" :min="0.01" :max="1" :step="0.01" v-model="fls.data.line2_ejector"></b-form-spinbutton>
                          </b-col>
                          <b-col class="text-center">
                            <label>Spyletrykk</label>
                            <b-form-spinbutton type="number" class="mb-3" :min="0.01" :max="1" :step="0.01" v-model="fls.data.line2_flushpressure"></b-form-spinbutton>
                          </b-col>
                          <b-col class="text-center">
                            <label>Oksygen</label>
                            <b-form-spinbutton type="number" class="mb-3" :min="1" :max="200" :step="1" v-model="fls.data.line2_oxygen"></b-form-spinbutton>
                          </b-col>
                        </b-row>
                        <hr />
                      </div>

                      <div v-if="fls.lines >= 3" class="mb-3">
                        <b-row>
                          <b-col>
                            <strong>Linje 3</strong>
                            <b-progress class="mb-1" height="0.5rem" :value="fls.data.line3_ejector" :max="1" variant="danger" animated></b-progress>
                            <b-progress class="mb-1" height="0.5rem" :value="fls.data.line3_flushpressure" :max="1" variant="warning" animated></b-progress>
                            <b-progress class="mb-1" height="0.5rem" :value="fls.data.line3_oxygen" :max="200" variant="info" animated></b-progress>
                          </b-col>
                          <b-col class="text-center">
                            <label>Ejektor trykk</label>
                            <b-form-spinbutton type="number" class="mb-3" :min="0.01" :max="1" :step="0.01" v-model="fls.data.line3_ejector"></b-form-spinbutton>
                          </b-col>
                          <b-col class="text-center">
                            <label>Spyletrykk</label>
                            <b-form-spinbutton type="number" class="mb-3" :min="0.01" :max="1" :step="0.01" v-model="fls.data.line3_flushpressure"></b-form-spinbutton>
                          </b-col>
                          <b-col class="text-center">
                            <label>Oksygen</label>
                            <b-form-spinbutton type="number" class="mb-3" :min="1" :max="200" :step="1" v-model="fls.data.line3_oxygen"></b-form-spinbutton>
                          </b-col>
                        </b-row>
                        <hr />
                      </div>

                      <div v-if="fls.lines >= 4" class="mb-3">
                        <b-row>
                          <b-col>
                            <strong>Linje 4</strong>
                            <b-progress class="mb-1" height="0.5rem" :value="fls.data.line4_ejector" :max="1" variant="danger" animated></b-progress>
                            <b-progress class="mb-1" height="0.5rem" :value="fls.data.line4_flushpressure" :max="1" variant="warning" animated></b-progress>
                            <b-progress class="mb-1" height="0.5rem" :value="fls.data.line4_oxygen" :max="200" variant="info" animated></b-progress>
                          </b-col>
                          <b-col class="text-center">
                            <label>Ejektor trykk</label>
                            <b-form-spinbutton type="number" class="mb-3" :min="0.01" :max="1" :step="0.01" v-model="fls.data.line4_ejector"></b-form-spinbutton>
                          </b-col>
                          <b-col class="text-center">
                            <label>Spyletrykk</label>
                            <b-form-spinbutton type="number" class="mb-3" :min="0.01" :max="1" :step="0.01" v-model="fls.data.line4_flushpressure"></b-form-spinbutton>
                          </b-col>
                          <b-col class="text-center">
                            <label>Oksygen</label>
                            <b-form-spinbutton type="number" class="mb-3" :min="1" :max="200" :step="1" v-model="fls.data.line4_oxygen"></b-form-spinbutton>
                          </b-col>
                        </b-row>
                      </div>

                      <b-table
                        v-if="offlineFlsDrafts.length > 0"
                        class="mt-3"
                        striped
                        hover
                        :items="offlineFlsTableItems"
                        :fields="flsDraftFields"
                      >
                        <template #cell(regtime)="data">
                          {{ $date(data.value).format("DD.MM.YYYY HH:mm") }}
                        </template>
                        <template #cell(cage)="data">
                          {{ data.item.from_cage }}
                          <span v-if="data.item.to_cage"> - {{ data.item.to_cage }}</span>
                        </template>
                        <template #cell(fishcount)="data">
                          {{ data.value | numeralFormat("0,0[.]00") }}
                        </template>
                        <template #cell(biomass)="data">
                          {{ data.value | numeralFormat("0,0[.]00") }} kg
                        </template>
                        <template #cell(actions)="row">
                          <b-button size="sm" variant="danger" @click="removeFlsDraft(row.item.id)">
                            Fjern
                          </b-button>
                        </template>
                      </b-table>
                    </b-card>
                  </b-col>
                </b-row>
              </b-card-text>
            </b-tab>

            <!-- Arbeidslogg Tab -->
            <b-tab title="Arbeidslogg">
              <b-card-text>
                <b-row>
                  <b-col md="3">
                    <b-card bg-variant="light">
                      <h6>Arbeidslogg</h6>
                      
                      <b-form-group label="Hendelse" class="mr-3">
                        <b-form-select v-model="worklog.action" :options="worklogOptions"></b-form-select>
                      </b-form-group>

                      <b-form inline class="mb-3">
                        <b-form-group label="Fra dato" class="mr-3">
                          <b-form-datepicker
                            id="startdate"
                            v-model="worklog.startdate"
                            @change="calculateWorklogDuration()"
                            :date-format-options="{ day: '2-digit', month: '2-digit', year: 'numeric' }"
                            locale="no"
                          ></b-form-datepicker>
                        </b-form-group>

                        <b-form-group label="Fra tidspunkt" class="mr-3">
                          <b-form-input
                            v-model="worklog.fromtime"
                            @change="calculateWorklogDuration()"
                            type="time"
                            placeholder="HH:mm"
                            locale="no"
                          ></b-form-input>
                        </b-form-group>
                      </b-form>

                      <b-form inline class="mb-3">
                        <b-form-group label="Til dato" class="mr-3">
                          <b-form-datepicker
                            id="enddate"
                            v-model="worklog.enddate"
                            @change="calculateWorklogDuration()"
                            :date-format-options="{ day: '2-digit', month: '2-digit', year: 'numeric' }"
                            locale="no"
                          ></b-form-datepicker>
                        </b-form-group>

                        <b-form-group label="Til tidspunkt" class="mr-3">
                          <b-form-input
                            v-model="worklog.totime"
                            @change="calculateWorklogDuration()"
                            type="time"
                            placeholder="HH:mm"
                            locale="no"
                          ></b-form-input>
                        </b-form-group>
                      </b-form>

                      <div>
                        <b-form-group label="Antall timer" class="mr-3">
                          <b-form-input
                            v-model.number="worklog.duration"
                            type="number"
                            placeholder="Antall timer"
                          ></b-form-input>
                        </b-form-group>
                      </div>

                      <b-form-textarea
                        class="mt-3"
                        v-model="worklog.description"
                        placeholder="Beskrivelse"
                        rows="4"
                        max-rows="6"
                      ></b-form-textarea>

                      <b-button class="mt-3" @click="saveWorklogOffline()" variant="primary">
                        Lagre som utkast
                      </b-button>
                    </b-card>
                  </b-col>

                  <b-col md="9">
                    <b-card>
                      <h6>Arbeidslogg Utkast (lagret lokalt)</h6>
                      
                      <b-alert v-if="offlineWorklogDrafts.length === 0" show variant="info">
                        Ingen arbeidslogg-utkast lagret ennå. Legg til arbeidslogg ved å fylle ut skjemaet til venstre.
                      </b-alert>

                      <b-table
                        v-if="offlineWorklogDrafts.length > 0"
                        striped
                        hover
                        :items="offlineWorklogTableItems"
                        :fields="worklogDraftFields"
                      >
                        <template #cell(from)="data">
                          {{ $date(data.value).format("DD.MM.YYYY HH:mm") }}
                        </template>
                        <template #cell(to)="data">
                          {{ $date(data.value).format("DD.MM.YYYY HH:mm") }}
                        </template>
                        <template #cell(duration)="data">
                          {{ data.value }} timer
                        </template>
                        <template #cell(actions)="row">
                          <b-button size="sm" variant="danger" @click="removeWorklogDraft(row.item.id)">
                            Fjern
                          </b-button>
                        </template>
                      </b-table>

                      <div v-if="offlineWorklogDrafts.length > 0" class="mt-3">
                        <p><strong>Totalt timer (utkast):</strong> {{ localWorklogSumHours }} timer</p>
                      </div>
                    </b-card>
                  </b-col>
                </b-row>
              </b-card-text>
            </b-tab>
          </b-tabs>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import axios from "axios";
import Vue2Filters from "vue2-filters";
import moment from "moment";
import { VueOfflineStorage } from 'vue-offline';

const OFFLINE_FLS_KEY_PREFIX = 'order-fls-drafts-';
const OFFLINE_WORKLOG_KEY_PREFIX = 'order-worklog-drafts-';
const OFFLINE_ORDER_SNAPSHOT_PREFIX = 'order-snapshot-';

export default {
  components: {
    VueOfflineStorage,
  },
  mixins: [Vue2Filters.mixin],
  props: ["id"],
  data() {
    return {
      order: {
        ordernumber: '',
        customer_company: null,
        site: null,
        type: '',
        details: {},
      },
      orderAvailable: false,
      fls: {
        lines: 1,
        from_cage: '',
        to_cage: '',
        fishcount: 0,
        biomass: 0,
        comment: '',
        completed: false,
        temperature_air: null,
        temperature_sea: null,
        lat: null,
        lng: null,
        data: {
          line1_ejector: 0,
          line1_flushpressure: 0,
          line1_oxygen: 0,
          line1_extrasuction: 0,
          line2_ejector: 0,
          line2_flushpressure: 0,
          line2_oxygen: 0,
          line2_extrasuction: 0,
          line3_ejector: 0,
          line3_flushpressure: 0,
          line3_oxygen: 0,
          line3_extrasuction: 0,
          line4_ejector: 0,
          line4_flushpressure: 0,
          line4_oxygen: 0,
          line4_extrasuction: 0,
        },
      },
      worklog: {
        action: "Reise",
        startdate: null,
        enddate: null,
        fromtime: '',
        totime: '',
        duration: 0,
        description: '',
      },
      offlineFlsDrafts: [],
      offlineWorklogDrafts: [],
      flsDraftFields: [
        { key: "regtime", label: "Tidspunkt" },
        { key: "cage", label: "Merder" },
        { key: "fishcount", label: "Antall fisk" },
        { key: "biomass", label: "Biomasse" },
        { key: "comment", label: "Kommentar" },
        { key: "actions", label: "" },
      ],
      worklogDraftFields: [
        { key: "action", label: "Hendelse" },
        { key: "from", label: "Fra" },
        { key: "to", label: "Til" },
        { key: "duration", label: "Timer" },
        { key: "description", label: "Beskrivelse" },
        { key: "actions", label: "" },
      ],
      worklogOptions: [
        { text: "Reise", value: "Reise" },
        { text: "Vent", value: "Vent" },
        { text: "Klargjøring", value: "Klargjøring" },
        { text: "Opprigg", value: "Opprigg" },
        { text: "Nedrigg", value: "Nedrigg" },
        { text: "Lasting", value: "Lasting" },
        { text: "Lossing", value: "Lossing" },
        { text: "Avlusning", value: "Avlusning" },
        { text: "Servicearbeid", value: "Servicearbeid" },
        { text: "Vasking", value: "Vasking" },
        { text: "Desinfeksjon", value: "Desinfeksjon" },
        { text: "Veterinær", value: "Veterinær" },
      ],
    };
  },
  computed: {
    $isOnline() {
      return this.$store ? this.$store.getters.isOnline : navigator.onLine;
    },
    offlineFlsTableItems() {
      return this.offlineFlsDrafts.map((draft) => ({
        id: draft.id,
        regtime: draft.payload.regtime,
        from_cage: draft.payload.from_cage,
        to_cage: draft.payload.to_cage,
        fishcount: draft.payload.fishcount,
        biomass: draft.payload.biomass,
        comment: draft.payload.comment,
      }));
    },
    offlineWorklogTableItems() {
      return this.offlineWorklogDrafts.map((draft) => ({
        id: draft.id,
        action: draft.payload.action,
        from: draft.payload.from,
        to: draft.payload.to,
        duration: draft.payload.duration,
        description: draft.payload.description,
      }));
    },
    localFlsSumFish() {
      return this.offlineFlsDrafts.reduce((sum, draft) => sum + (draft.payload.fishcount || 0), 0);
    },
    localFlsSumBiomass() {
      return this.offlineFlsDrafts.reduce((sum, draft) => sum + (draft.payload.biomass || 0), 0);
    },
    localWorklogSumHours() {
      return this.offlineWorklogDrafts.reduce((sum, draft) => sum + (draft.payload.duration || 0), 0);
    },
  },
  mounted() {
    this.loadOrder();
    this.loadOfflineDrafts();
  },
  methods: {
    loadOrder() {
      if (!this.$offlineStorage) {
        this.orderAvailable = false;
        return;
      }

      const snapshotKey = `${OFFLINE_ORDER_SNAPSHOT_PREFIX}${this.id}`;
      const snapshot = this.$offlineStorage.get(snapshotKey);

      if (snapshot) {
        this.order = JSON.parse(JSON.stringify(snapshot));
        this.orderAvailable = true;

        // Initialize FLS settings from last entry
        if (this.order.fls && this.order.fls.length > 0) {
          const lastFls = this.order.fls[this.order.fls.length - 1];
          this.fls.lines = lastFls.lines || 1;
          this.fls.data = { ...lastFls.data };
        }
      } else {
        this.orderAvailable = false;
      }
    },
    loadOfflineDrafts() {
      if (!this.$offlineStorage) {
        return;
      }

      const flsKey = `${OFFLINE_FLS_KEY_PREFIX}${this.id}`;
      const worklogKey = `${OFFLINE_WORKLOG_KEY_PREFIX}${this.id}`;

      const flsDrafts = this.$offlineStorage.get(flsKey);
      const worklogDrafts = this.$offlineStorage.get(worklogKey);

      this.offlineFlsDrafts = Array.isArray(flsDrafts) ? flsDrafts : [];
      this.offlineWorklogDrafts = Array.isArray(worklogDrafts) ? worklogDrafts : [];
    },
    saveFlsOffline() {
      if (!this.fls.from_cage || !this.fls.fishcount || !this.fls.biomass) {
        this.$bvToast.toast('Vennligst fyll ut alle obligatoriske felt (Fra merd, antall fisk, biomasse)', {
          title: 'Mangler data',
          variant: 'warning',
          solid: true,
        });
        return;
      }

      const site = this.order.site || {};
      const vessel = this.order.vessel || {};
      const customerCompany = this.order.customer_company || {};
      const supplierCompany = this.order.supplier_company || {};
      const flsMetrics = this.fls.data || {};

      const flsData = {
        order: this.order.id,
        ordernumber: this.order.ordernumber,
        site: site.name,
        number: site.number,
        vessel: vessel.name,
        customer: customerCompany.name,
        company: supplierCompany.id,
        lat: this.fls.lat,
        lng: this.fls.lng,
        lines: this.fls.lines,
        from_cage: this.fls.from_cage,
        to_cage: this.fls.to_cage || '',
        regtime: new Date().toISOString(),
        comment: this.fls.comment || '',
        temperature_air: this.fls.temperature_air,
        temperature_sea: this.fls.temperature_sea,
        fishcount: this.fls.fishcount,
        biomass: this.fls.biomass,
        completed: this.fls.completed,
        data: {
          line1_ejector: flsMetrics.line1_ejector,
          line2_ejector: flsMetrics.line2_ejector,
          line3_ejector: flsMetrics.line3_ejector,
          line4_ejector: flsMetrics.line4_ejector,
          line1_extrasuction: flsMetrics.line1_extrasuction,
          line2_extrasuction: flsMetrics.line2_extrasuction,
          line3_extrasuction: flsMetrics.line3_extrasuction,
          line4_extrasuction: flsMetrics.line4_extrasuction,
          line1_flushpressure: flsMetrics.line1_flushpressure,
          line2_flushpressure: flsMetrics.line2_flushpressure,
          line3_flushpressure: flsMetrics.line3_flushpressure,
          line4_flushpressure: flsMetrics.line4_flushpressure,
          line1_oxygen: flsMetrics.line1_oxygen,
          line2_oxygen: flsMetrics.line2_oxygen,
          line3_oxygen: flsMetrics.line3_oxygen,
          line4_oxygen: flsMetrics.line4_oxygen,
        },
      };

      const draft = {
        id: `fls-draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        createdAt: new Date().toISOString(),
        payload: flsData,
      };

      this.offlineFlsDrafts.push(draft);
      this.persistOfflineFlsDrafts();

      this.$bvToast.toast('FLS lagret som utkast. Det vil bli lastet opp når du er online.', {
        title: 'FLS lagret',
        variant: 'success',
        solid: true,
      });

      // Reset form
      this.fls.from_cage = '';
      this.fls.to_cage = '';
      this.fls.fishcount = 0;
      this.fls.biomass = 0;
      this.fls.comment = '';
      this.fls.completed = false;
    },
    saveWorklogOffline() {
      if (!this.worklog.action || !this.worklog.startdate || !this.worklog.enddate) {
        this.$bvToast.toast('Vennligst fyll ut hendelse, fra dato og til dato', {
          title: 'Mangler data',
          variant: 'warning',
          solid: true,
        });
        return;
      }

      const site = this.order.site || {};
      const customerCompany = this.order.customer_company || {};
      const supplierCompany = this.order.supplier_company || {};

      const from = moment(
        this.worklog.startdate + ", " + (this.worklog.fromtime || '00:00')
      ).locale("no");

      const to = moment(
        this.worklog.enddate + ", " + (this.worklog.totime || '00:00')
      ).locale("no");

      const worklogData = {
        action: this.worklog.action,
        billable: true,
        billtype: "Timepris",
        rate: 0,
        total: 0,
        from: from.toISOString(),
        to: to.toISOString(),
        duration: this.worklog.duration,
        description: this.worklog.description || '',
        order: this.order.id,
        ordernumber: this.order.ordernumber,
        site: site.name,
        number: site.number,
        customer: customerCompany.name,
        company: supplierCompany.id,
      };

      const draft = {
        id: `worklog-draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        createdAt: new Date().toISOString(),
        payload: worklogData,
      };

      this.offlineWorklogDrafts.push(draft);
      this.persistOfflineWorklogDrafts();

      this.$bvToast.toast('Arbeidslogg lagret som utkast. Det vil bli lastet opp når du er online.', {
        title: 'Arbeidslogg lagret',
        variant: 'success',
        solid: true,
      });

      // Reset form
      this.worklog.action = "Reise";
      this.worklog.startdate = null;
      this.worklog.enddate = null;
      this.worklog.fromtime = '';
      this.worklog.totime = '';
      this.worklog.duration = 0;
      this.worklog.description = '';
    },
    calculateWorklogDuration() {
      if (!this.worklog.startdate || !this.worklog.enddate) {
        return;
      }

      const start = moment(`${this.worklog.startdate} ${this.worklog.fromtime || '00:00'}`);
      const end = moment(`${this.worklog.enddate} ${this.worklog.totime || '00:00'}`);

      const durationHours = end.diff(start, 'minutes') / 60;
      const roundedDuration = Math.round(durationHours / 0.5) * 0.5;

      this.worklog.duration = roundedDuration;
    },
    persistOfflineFlsDrafts() {
      if (this.$offlineStorage) {
        const flsKey = `${OFFLINE_FLS_KEY_PREFIX}${this.id}`;
        this.$offlineStorage.set(flsKey, this.offlineFlsDrafts);
      }
    },
    persistOfflineWorklogDrafts() {
      if (this.$offlineStorage) {
        const worklogKey = `${OFFLINE_WORKLOG_KEY_PREFIX}${this.id}`;
        this.$offlineStorage.set(worklogKey, this.offlineWorklogDrafts);
      }
    },
    removeFlsDraft(draftId) {
      this.offlineFlsDrafts = this.offlineFlsDrafts.filter(d => d.id !== draftId);
      this.persistOfflineFlsDrafts();
      
      this.$bvToast.toast('FLS-utkast fjernet', {
        title: 'Fjernet',
        variant: 'info',
        solid: true,
      });
    },
    removeWorklogDraft(draftId) {
      this.offlineWorklogDrafts = this.offlineWorklogDrafts.filter(d => d.id !== draftId);
      this.persistOfflineWorklogDrafts();
      
      this.$bvToast.toast('Arbeidslogg-utkast fjernet', {
        title: 'Fjernet',
        variant: 'info',
        solid: true,
      });
    },
    goBack() {
      this.$router.go(-1);
    },
    goToOnlineEditor() {
      this.$router.push({ name: 'order-edit', params: { id: this.id } });
    },
  },
};
</script>

<style scoped>
.notemaxwidth {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
